/**
 * InstallBanner — barra proactiva de instalación PWA.
 * Aparece cuando el browser dispara `beforeinstallprompt` (Android/Chrome/Edge).
 * Se descarta para la sesión actual; no molesta en iOS (que no tiene el evento).
 */
import { useState, useEffect } from "react";
import { Download } from "lucide-react";

const esIOS = () => typeof navigator !== "undefined" && (/iPhone|iPad|iPod/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1));
const instalada = () => typeof window !== "undefined" && (window.matchMedia?.("(display-mode: standalone)").matches || navigator.standalone === true);
const CLAVE_IOS = "portal_ios_instalar_hasta";

/** iPhone/iPad no dispara `beforeinstallprompt`: se instala a mano desde Compartir. Sin instalar no hay avisos en iOS. */
function GuiaIOS() {
  const [ver, setVer] = useState(() => {
    if (!esIOS() || instalada()) return false;
    try { return Number(localStorage.getItem(CLAVE_IOS) || 0) < Date.now(); } catch { return true; }
  });
  if (!ver) return null;
  const cerrar = () => { try { localStorage.setItem(CLAVE_IOS, String(Date.now() + 14 * 24 * 3600 * 1000)); } catch { /* sin storage */ } setVer(false); };
  return (
    <div role="region" aria-label="Instalar en iPhone"
         style={{ position: "fixed", left: 12, right: 12, bottom: "calc(96px + env(safe-area-inset-bottom))", zIndex: 9998, background: "var(--paper, #fff)", color: "var(--ink, #111318)", borderRadius: 20, padding: 16, boxShadow: "0 4px 16px rgba(17,19,24,0.18)" }}>
      <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ margin: 0, fontSize: 15, fontWeight: 800 }}>Instalá el portal en tu iPhone</p>
          <p style={{ margin: "4px 0 0", fontSize: 13, lineHeight: 1.35, opacity: 0.75 }}>Tocá <strong>Compartir</strong> (el cuadrado con la flecha) y elegí <strong>«Agregar a pantalla de inicio»</strong>. Así se abre como app y podés recibir avisos.</p>
        </div>
        <button onClick={cerrar} aria-label="Cerrar" style={{ border: 0, background: "transparent", fontSize: 20, lineHeight: 1, cursor: "pointer", color: "inherit", opacity: 0.6 }}>×</button>
      </div>
    </div>
  );
}

export function InstallBanner() {
  if (esIOS()) return <GuiaIOS />;
  return <InstallBannerAndroid />;
}

function InstallBannerAndroid() {
  const [prompt,    setPrompt]    = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [installed, setInstalled] = useState(false);
  // v8.5.49 · piel · "nunca los dos banners a la vez: el de actualización
  // gana" — mismo evento que ya escucha SwUpdateBanner.jsx; una vez que hay
  // una versión nueva disponible, este banner se queda afuera por el resto
  // de la sesión de la pestaña, sin necesitar un store compartido nuevo.
  const [swUpdateAvailable, setSwUpdateAvailable] = useState(false);

  useEffect(() => {
    // Ya descartado esta sesión
    if (sessionStorage.getItem("pwa-install-dismissed")) { setDismissed(true); return; }
    // Ya instalada
    if (window.matchMedia("(display-mode: standalone)").matches) { setInstalled(true); return; }

    const onPrompt = (e) => { e.preventDefault(); setPrompt(e); };
    const onInstalled = () => setInstalled(true);
    const onSwUpdate = () => setSwUpdateAvailable(true);

    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    window.addEventListener("sw-update-available", onSwUpdate);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
      window.removeEventListener("sw-update-available", onSwUpdate);
    };
  }, []);

  if (!prompt || dismissed || installed || swUpdateAvailable) return null;

  const handleInstall = async () => {
    prompt.prompt();
    const { outcome } = await prompt.userChoice;
    if (outcome === "accepted") setInstalled(true);
    setPrompt(null);
  };

  const handleDismiss = () => {
    setDismissed(true);
    sessionStorage.setItem("pwa-install-dismissed", "1");
  };

  return (
    // Tarjeta flotante abajo (antes una franja fija arriba que tapaba el logo del cliente, el
    // buscador y la barra superior). En celular queda sobre la barra inferior.
    <div role="banner"
         className="fixed left-1/2 -translate-x-1/2 bottom-[88px] sm:bottom-5 z-[var(--z-notice)] flex items-center gap-4
                    px-4 py-2.5 rounded-[16px] shadow-[0_4px_16px_rgba(17,19,24,0.14)] max-w-[calc(100vw-24px)]
                    bg-[var(--accent-soft)] border border-[var(--accent-line)] text-[14px] text-[var(--accent-ink)]"
         style={{ animation: "swBannerIn 0.3s ease-out" }}>
      <span className="flex items-center gap-2 whitespace-nowrap">
        <Download className="h-4 w-4 shrink-0" />
        Instalá el portal en tu teléfono
      </span>
      <button
        onClick={handleInstall}
        className="font-medium hover:underline whitespace-nowrap">
        Instalar
      </button>
      <button
        onClick={handleDismiss}
        className="hover:underline whitespace-nowrap opacity-80">
        Ahora no
      </button>
    </div>
  );
}
